import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

const ChartPage1 = ({ filtered }) => {
  const dailyProfit = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const day = t.open.toISOString().split("T")[0];
      map[day] = (map[day] || 0) + t.profit;
    });
    return Object.entries(map)
      .sort()
      .map(([day, profit]) => ({ day, profit }));
  }, [filtered]);

  const tradesPerDay = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const day = t.open.toISOString().split("T")[0];
      map[day] = (map[day] || 0) + 1;
    });
    return Object.entries(map)
      .sort()
      .map(([day, count]) => ({ day, count }));
  }, [filtered]);

  const dailyWinRate = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const day = t.open.toISOString().split("T")[0];
      if (!map[day]) {
        map[day] = { wins: 0, total: 0 };
      }
      map[day].total += 1;
      if (t.profit > 0) map[day].wins += 1;
    });
    return Object.entries(map)
      .sort()
      .map(([day, { wins, total }]) => ({
        day,
        winRate: total > 0 ? ((wins / total) * 100).toFixed(1) : 0,
      }));
  }, [filtered]);

  const profitData = useMemo(() => {
    return filtered.map((t, i) => ({ idx: i + 1, profit: t.profit }));
  }, [filtered]);

  const profitByWeekday = useMemo(() => {
    const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    const count = Array(7).fill(0);
    const sum = Array(7).fill(0);

    filtered.forEach((t) => {
      const i = t.open.getDay();
      count[i] += 1;
      sum[i] += t.profit;
    });

    return days.map((label, i) => ({
      day: label,
      avg: count[i] > 0 ? +(sum[i] / count[i]).toFixed(0) : 0,
    }));
  }, [filtered]);

  const avgProfitLoss = useMemo(() => {
    const winners = filtered.filter((t) => t.profit > 0);
    const losers = filtered.filter((t) => t.profit < 0);

    const avgWin = winners.length
      ? winners.reduce((sum, t) => sum + t.profit, 0) / winners.length
      : 0;
    const avgLoss = losers.length
      ? losers.reduce((sum, t) => sum + t.profit, 0) / losers.length
      : 0;

    return [
      { type: "Promedio Ganancia", valor: +avgWin.toFixed(0) },
      { type: "Promedio Pérdida", valor: +avgLoss.toFixed(0) },
    ];
  }, [filtered]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Profit por Día */}
      <div className="p-4 bg-[#1f2a40] rounded shadow">
        <h3 className="mb-2 font-semibold">Profit por Día</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={dailyProfit}>
            <CartesianGrid stroke="#2a344b" />
            <XAxis dataKey="day" tick={{ fill: "#cbd5e1", fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={60} />
            <YAxis tick={{ fill: "#cbd5e1" }} />
            <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
            <Bar dataKey="profit" fill="#34d399" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cantidad de Trades por Día */}
      <div className="p-4 bg-[#1f2a40] rounded shadow">
        <h3 className="mb-2 font-semibold">Cantidad de Trades por Día</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={tradesPerDay}>
            <CartesianGrid stroke="#2a344b" />
            <XAxis dataKey="day" tick={{ fill: "#cbd5e1", fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={60} />
            <YAxis tick={{ fill: "#cbd5e1" }} />
            <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
            <Bar dataKey="count" fill="#fbbf24" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Win Rate Diario */}
      <div className="p-4 bg-[#1f2a40] rounded shadow col-span-1 lg:col-span-2">
        <h3 className="mb-2 font-semibold">Win Rate Diario</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={dailyWinRate}>
            <CartesianGrid stroke="#2a344b" />
            <XAxis dataKey="day" tick={{ fill: "#cbd5e1", fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={60} />
            <YAxis tick={{ fill: "#cbd5e1" }} domain={[0, 100]} />
            <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
            <Line type="monotone" dataKey="winRate" stroke="#38bdf8" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Profit por Trade */}
      <div className="p-4 bg-[#1f2a40] rounded shadow">
        <h3 className="mb-2 font-semibold">Profit por Trade</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={profitData}>
            <CartesianGrid stroke="#2a344b" />
            <XAxis dataKey="idx" tick={{ fill: "#cbd5e1" }} />
            <YAxis tick={{ fill: "#cbd5e1" }} />
            <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
            <Bar dataKey="profit" fill="#f472b6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Profit Promedio por Día de la Semana */}
      <div className="p-4 bg-[#1f2a40] rounded shadow">
        <h3 className="mb-2 font-semibold">Profit Promedio por Día de la Semana</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={profitByWeekday}>
            <CartesianGrid stroke="#2a344b" />
            <XAxis dataKey="day" tick={{ fill: "#cbd5e1" }} />
            <YAxis tick={{ fill: "#cbd5e1" }} />
            <Tooltip />
            <Bar dataKey="avg" fill="#60a5fa" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tamaño Promedio Ganancias vs Pérdidas */}
      <div className="p-4 bg-[#1f2a40] rounded shadow">
        <h3 className="mb-2 font-semibold">Tamaño Promedio: Ganancias vs Pérdidas</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={avgProfitLoss}>
            <CartesianGrid stroke="#2a344b" />
            <XAxis dataKey="type" tick={{ fill: "#cbd5e1" }} />
            <YAxis tick={{ fill: "#cbd5e1" }} />
            <Tooltip />
            <Bar dataKey="valor" fill="#facc15" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartPage1;
